import {Dispatch, FC, MouseEvent, SetStateAction, useEffect, useRef, useState} from 'react'
import {CustomInputs} from '../../../../../../../../_metronic/helpers/components/CustomInputs'
import {API_URL, isNotEmpty} from '../../../../../../../../_metronic/helpers'
import axios from 'axios'
import {Product} from '../../../../../product-management/products-list/core/_models'
import clsx from 'clsx'
import {setProductFavorite} from '../../../../../product-management/products-list/core/_requests'
import './selectProduct.css'
import {useAuth} from '../../../../../../auth'

type props = {
  productSelected: {
    productSelected: Product | undefined
    setProductSelected: Dispatch<SetStateAction<Product | undefined>>
  }
  invoiceDate: string
  isFavorite?: boolean
}

const SelectProduct: FC<props> = ({productSelected, invoiceDate, isFavorite = false}) => {
  const {currentUser} = useAuth()
  const valueAddedNotCalled =
    currentUser?.cpl?.find((company) => company.id == currentUser.cpi)?.vnc == true
  const [first, setFirst] = useState(true)
  const [search, setSearch] = useState('')
  const [loading, setLoading] = useState(false)
  const [productList, setProductList] = useState<Product[]>()
  const [favoriteSuggestion, setFavoriteSuggestion] = useState<Product>()
  const [favoriteSuggestionLoading, setFavoriteSuggestionLoading] = useState(false)
  const [favoriteUpdatingId, setFavoriteUpdatingId] = useState<Product['id']>()
  const cancelTokenSource = useRef<any | null>(null)
  const suggestionCancelTokenSource = useRef<any | null>(null)
  const hasSearch = isNotEmpty(search)
  const showFavoriteEmptyHelp = isFavorite && productList && productList.length == 0 && !hasSearch
  const pickerTitle = isFavorite ? 'کالا/خدمت‌های محبوب' : 'انتخاب از کالا/خدمت‌ها'
  const pickerHint = isFavorite
    ? 'موارد پرتکرار را سریع انتخاب کنید یا از همین‌جا از علاقه‌مندی‌ها خارج کنید.'
    : 'با نام، کد داخلی یا شناسه جستجو کنید و روی کارت موردنظر بزنید.'
  const searchPlaceholder = isFavorite
    ? 'جستجو در علاقه‌مندی‌ها...'
    : 'نام، کد داخلی یا شناسه کالا/خدمت...'

  function getProduct() {
    if (cancelTokenSource.current) {
      cancelTokenSource.current.cancel('Operation canceled by the user.')
    }

    cancelTokenSource.current = axios.CancelToken.source()
    setLoading(true)

    const params = new URLSearchParams()
    params.set('search', search)
    params.set('date', invoiceDate || '')
    params.set('isFavorite', isFavorite ? '1' : '0')
    params.set('items_per_page', '30')
    params.set('sort', 'usage')
    params.set('order', 'desc')

    axios
      .get(`${API_URL}/products/query?${params.toString()}`, {
        cancelToken: cancelTokenSource.current.token,
      })
      .then((response) => {
        setProductList(response.data.data)
        setLoading(false)
      })
      .catch((thrown) => {
        setLoading(false)
        if (axios.isCancel(thrown)) {
          console.error('Request canceled', thrown.message)
        } else {
          // handle error
        }
      })
  }

  function getFavoriteSuggestion() {
    if (suggestionCancelTokenSource.current) {
      suggestionCancelTokenSource.current.cancel('Operation canceled by the user.')
    }

    suggestionCancelTokenSource.current = axios.CancelToken.source()
    setFavoriteSuggestionLoading(true)

    const params = new URLSearchParams()
    params.set('date', invoiceDate || '')
    params.set('isFavorite', '0')
    params.set('items_per_page', '1')
    params.set('sort', 'usage')
    params.set('order', 'desc')

    axios
      .get(`${API_URL}/products/query?${params.toString()}`, {
        cancelToken: suggestionCancelTokenSource.current.token,
      })
      .then((response) => {
        setFavoriteSuggestion((response.data.data || [])[0])
        setFavoriteSuggestionLoading(false)
      })
      .catch((thrown) => {
        setFavoriteSuggestionLoading(false)
        if (axios.isCancel(thrown)) {
          console.error('Request canceled', thrown.message)
        }
      })
  }

  const toggleFavorite = async (product: Product, event: MouseEvent) => {
    event.preventDefault()
    event.stopPropagation()

    if (product.id === undefined || product.id === null || favoriteUpdatingId === product.id) {
      return
    }

    const nextFavorite = !product.fav
    try {
      setFavoriteUpdatingId(product.id)
      const updatedProduct = await setProductFavorite(product.id, nextFavorite)
      setProductList((current) => {
        const items = current || []
        if (isFavorite && !nextFavorite) {
          return items.filter((item) => item.id !== product.id)
        }

        if (isFavorite && nextFavorite) {
          const favoriteProduct = {
            ...product,
            ...(updatedProduct || {}),
            fav: true,
          }

          if (!items.some((item) => item.id === product.id)) {
            return [favoriteProduct, ...items]
          }

          return items.map((item) =>
            item.id === product.id
              ? {
                  ...item,
                  ...favoriteProduct,
                }
              : item
          )
        }

        return items.map((item) =>
          item.id === product.id
            ? {
                ...item,
                fav: nextFavorite,
              }
            : item
        )
      })

      if (productSelected.productSelected?.id === product.id) {
        productSelected.setProductSelected({
          ...productSelected.productSelected,
          fav: nextFavorite,
        })
      }

      if (favoriteSuggestion?.id === product.id) {
        setFavoriteSuggestion(nextFavorite ? undefined : {...product, fav: nextFavorite})
      }
    } catch (error: any) {
      alert(
        error?.response?.data?.Message ||
          error?.response?.data?.ExceptionMessage ||
          error?.message ||
          'خطا در تغییر لیست علاقه‌مندی‌ها'
      )
    } finally {
      setFavoriteUpdatingId(undefined)
    }
  }

  useEffect(() => {
    if (first) {
      getProduct()
      setFirst(false)
    }
  }, [])

  useEffect(() => {
    if (!first) {
      getProduct()
    }
  }, [search])

  useEffect(() => {
    if (showFavoriteEmptyHelp && !favoriteSuggestion && !favoriteSuggestionLoading) {
      getFavoriteSuggestion()
    }
  }, [showFavoriteEmptyHelp])

  return (
    <>
      <div className='row mp-product-picker-content'>
        {!showFavoriteEmptyHelp && (
          <div className='col-12'>
            <div className='mp-product-picker-head'>
              <div>
                <div className='mp-product-picker-title'>{pickerTitle}</div>
                <div className='mp-product-picker-hint'>{pickerHint}</div>
              </div>
            </div>
          </div>
        )}
        {!showFavoriteEmptyHelp && (
          <div className='col-12'>
            <CustomInputs
              type='text'
              placeholder={searchPlaceholder}
              stateValue={{
                value: search,
                setValue: setSearch,
              }}
            />
          </div>
        )}
        <div className='col-12'>
          {(loading || !productList) && (
            <div className='text-center p-10'>
              <span className='spinner-border spinner-border-sm align-middle me-2 text-primary'></span>
              در حال پردازش...
            </div>
          )}
          {productList && (
            <>
              {productList.map((itm, ind) => {
                const displayVatRate = valueAddedNotCalled ? 0 : itm.vtr || 0
                return (
                  <div
                    key={ind}
                    className={clsx('mp-product-pick-card', {
                      selected: productSelected.productSelected?.id === itm.id,
                    })}
                    onClick={() => {
                      productSelected.setProductSelected(itm)
                    }}
                  >
                    <div className='mp-product-pick-main'>
                      <div className='mp-product-pick-name'>{itm.nm}</div>
                      <div className='mp-product-pick-meta'>
                        {isNotEmpty(itm.sti) && <span>{itm.sti}</span>}
                        {itm.typ && (
                          <span className='badge badge-light-info'>
                            <i className={clsx('las me-1', itm.typ == 1 ? 'la-box' : 'la-hands-helping')}></i>
                            {itm.typ == 1 ? 'کالا' : 'خدمت'}
                          </span>
                        )}
                        <span className={clsx('badge', displayVatRate == 0 ? 'badge-light-success' : 'badge-light-primary')}>
                          {displayVatRate == 0 ? 'معاف از مالیات' : `مالیات ${displayVatRate}%`}
                        </span>
                      </div>
                      {isNotEmpty(itm.cd) && itm.cd != itm.sti && (
                        <div className='mp-product-pick-code'>{itm.cd}</div>
                      )}
                    </div>
                    <button
                      type='button'
                      className='mp-product-pick-fav-btn'
                      onClick={(e) => toggleFavorite(itm, e)}
                      title={itm.fav ? 'حذف از علاقه‌مندی‌ها' : 'افزودن به علاقه‌مندی‌ها'}
                    >
                      {favoriteUpdatingId === itm.id ? (
                        <span
                          className='spinner-border spinner-border-sm text-warning'
                          role='status'
                          aria-hidden='true'
                          style={{width: '1.75rem', height: '1.75rem'}}
                        ></span>
                      ) : (
                        <i
                          className={clsx(
                            'las la-star fs-2x',
                            itm.fav ? 'text-warning' : 'text-gray-400'
                          )}
                        ></i>
                      )}
                    </button>
                  </div>
                )
              })}
            </>
          )}
          {showFavoriteEmptyHelp && (
            <div className='mp-invoice-product-empty-fav text-center'>
              <div className='mp-empty-fav-icon-wrap'>
                <i className='las la-star mp-empty-fav-star'></i>
                <i className='las la-plus-circle mp-empty-fav-plus'></i>
              </div>
              <div className='fw-bolder fs-3 mt-4'>لیست علاقه‌مندی‌ها خالی است</div>
              <div className='text-gray-700 mt-3 mx-auto mp-empty-fav-copy'>
                کالا/خدمت‌های پرتکرار را ستاره‌دار کنید تا هنگام صدور صورتحساب سریع‌تر انتخاب شوند.
                یک نمونه از لیست شما آماده است؛ روی ستاره بزنید تا همین‌جا اضافه شود.
              </div>
              <div className='mp-empty-fav-preview-wrap'>
                {favoriteSuggestionLoading && (
                  <div className='mp-empty-fav-preview-loading'>
                    <span className='spinner-border spinner-border-sm text-primary me-2'></span>
                    در حال آماده‌کردن یک پیشنهاد...
                  </div>
                )}
                {!favoriteSuggestionLoading && favoriteSuggestion && (
                  <div className='mp-empty-fav-preview-card'>
                    <div className='mp-empty-fav-preview-content'>
                      <div className='fw-bolder text-gray-800'>{favoriteSuggestion.nm}</div>
                      <div className='text-gray-600 fs-7 mt-1'>
                        {favoriteSuggestion.sti}
                        {favoriteSuggestion.typ && (
                          <span className='badge badge-light-info ms-2'>
                            {favoriteSuggestion.typ == 1 ? 'کالا' : 'خدمت'}
                          </span>
                        )}
                      </div>
                    </div>
                    <button
                      type='button'
                      className='btn btn-sm btn-icon btn-light-warning mp-empty-fav-preview-star'
                      onClick={(e) => toggleFavorite({...favoriteSuggestion, fav: false}, e)}
                      title='افزودن به علاقه‌مندی‌ها'
                    >
                      {favoriteUpdatingId === favoriteSuggestion.id ? (
                        <span className='spinner-border spinner-border-sm text-warning'></span>
                      ) : (
                        <i className='las la-star fs-2x text-gray-400'></i>
                      )}
                    </button>
                    <div className='mp-empty-fav-click-hint'>
                      <i className='las la-level-up-alt'></i>
                      اینجا کلیک کنید
                    </div>
                  </div>
                )}
                {!favoriteSuggestionLoading && !favoriteSuggestion && (
                  <div className='mp-empty-fav-preview-loading text-gray-600'>
                    هنوز کالا/خدمتی برای پیشنهاد وجود ندارد.
                  </div>
                )}
              </div>
              <div className='mp-empty-fav-help mt-5'>
                <div className='mp-empty-fav-help-item'>
                  <i className='las la-list-alt text-primary'></i>
                  <span>از «کالا/خدمت شما» هم می‌توانید هر موردی را ستاره‌دار کنید.</span>
                </div>
                <div className='mp-empty-fav-help-item'>
                  <i className='las la-star text-warning'></i>
                  <span>ستاره یعنی این مورد در شروع انتخاب‌ها جلوی دستتان باشد.</span>
                </div>
              </div>
            </div>
          )}
          {productList && productList.length == 0 && !showFavoriteEmptyHelp && (
            <div className='text-center p-10'>
              {isFavorite
                ? 'در علاقه‌مندی‌ها نتیجه‌ای پیدا نشد.'
                : 'کالا/خدمت یافت نشد.'}
            </div>
          )}
        </div>
      </div>
    </>
  )
}

export {SelectProduct}
